import type { AppRouter } from "@repo/api";
import {
  httpBatchLink,
  httpLink,
  httpSubscriptionLink,
  splitLink,
  type TRPCLink,
} from "@trpc/client";

import { EdgeHttpError, parseRetryAfterMs } from "./edge-errors";
import { getPostHogSessionHeader } from "./posthog-session-header";
import { getClientTimezoneHeader } from "./timezone-header";
import { isUnbatched } from "./unbatched";

const REQUEST_TIMEOUT_MS = 30_000;

async function fetchWithEdgeErrors(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(input, {
    ...init,
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
  const isJson =
    response.headers.get("content-type")?.includes("application/json") ?? false;
  // A non-JSON error body is the platform's, not tRPC's: see EdgeHttpError.
  if (!response.ok && !isJson) {
    throw new EdgeHttpError(
      response.status,
      parseRetryAfterMs(response.headers.get("retry-after")),
    );
  }
  return response;
}

export function createTrpcLinks(baseUrl: string): TRPCLink<AppRouter>[] {
  const http = {
    url: `${baseUrl}/api/trpc`,
    headers: () => ({
      ...getClientTimezoneHeader(),
      ...getPostHogSessionHeader(),
    }),
    fetch: fetchWithEdgeErrors,
  };

  return [
    splitLink({
      condition: (op) => op.type === "subscription",
      true: httpSubscriptionLink({ url: http.url }),
      false: splitLink({
        condition: isUnbatched,
        true: httpLink(http),
        false: httpBatchLink(http),
      }),
    }),
  ];
}
